import { memo, useEffect, useMemo, useRef, useState } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledLazyQuery from "apollo/usePolledQuery/usePolledLazyQuery";
import Filters from "components/Filters";
import FiltersContentWrapper from "components/Filters/ContentWrapper";
import { useCachedFilterFields } from "components/Filters/hooks";
import FiltersSidebar from "components/Filters/Sidebar";
import FiltersSplit from "components/Filters/Split";
import { SavedFilterView } from "components/Filters/types";
import FlashContext from "components/FlashMessages/FlashContext";
import { getSearchQuery } from "components/SearchInput/helpers";
import { searchAnsibleHostsSuggestionsDictionary } from "constants/ansibleHosts";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import useTypedContext from "hooks/useTypedContext";
import useURLParams from "hooks/useURLParams";
import { SearchQueryPredicate, SearchSuggestionsField } from "types/generated";
import useMakeFiltersData from "components/Filters/useMakeFiltersData";

import { DEPRECATED_FILTER_NAMES, FILTERS_ORDER_SETTINGS_KEY } from "../constants";
import { SEARCH_ANSIBLE_HOSTS_SUGGESTIONS, SEARCH_ANSIBLE_TASKS_SUGGESTIONS } from "../gql";
import { AnsibleGroupBy } from "../types";

type ConfigManagementFiltersLayoutProps = {
  predicates: SearchQueryPredicate[];
  children: React.ReactNode;
  groupBy: AnsibleGroupBy;
  hiddenFilters?: string[];
  filtersType: string;
};

const ConfigManagementFiltersLayout = ({
  predicates,
  children,
  groupBy,
  hiddenFilters,
  filtersType,
}: ConfigManagementFiltersLayoutProps) => {
  const [currentSavedView, setCurrentSavedView] = useState<SavedFilterView | undefined>();
  const fieldsRef = useRef<string[] | null>(null);

  const urlParams = useURLParams();
  const searchInput = getSearchQuery(urlParams);

  const { onError } = useTypedContext(FlashContext);

  const [
    loadSearchAnsibleHostsSuggestions,
    { data: filteringHostsData, loading: hostsLoading, refetch: hostsRefetch },
  ] = usePolledLazyQuery(SEARCH_ANSIBLE_HOSTS_SUGGESTIONS, {
    pollingGroup: PollingIntervalGroups.Lists,
  });

  const [
    loadSearchAnsibleTasksSuggestions,
    { data: filteringTasksData, loading: tasksLoading, refetch: tasksRefetch },
  ] = usePolledLazyQuery(SEARCH_ANSIBLE_TASKS_SUGGESTIONS, {
    pollingGroup: PollingIntervalGroups.Lists,
  });

  const cachedHostsFiltersData = useCachedFilterFields(
    filteringHostsData?.searchAnsibleHostsSuggestions?.fields as SearchSuggestionsField[]
  );

  const cachedTasksFiltersData = useCachedFilterFields(
    filteringTasksData?.searchAnsibleTasksSuggestions?.fields as SearchSuggestionsField[]
  );

  const cachedFiltersData =
    groupBy === AnsibleGroupBy.Hosts ? cachedHostsFiltersData : cachedTasksFiltersData;

  const { filters, filterOptionsMap } = useMakeFiltersData({
    suggestions: cachedFiltersData,
    dictionary: searchAnsibleHostsSuggestionsDictionary,
  });

  // Unmount/navigation mid-request aborts the pending query - not a real failure,
  // so don't surface it to the user.
  const reportIfNotAborted = (error: Error) => {
    if (error?.name !== "AbortError") {
      onError(error);
    }
  };

  const loadSearchSuggestions = (initial = false) => {
    const variables = {
      input: {
        fullTextSearch: searchInput,
        predicates,
        fields: initial ? null : fieldsRef.current,
      },
    };

    if (groupBy === AnsibleGroupBy.Hosts) {
      loadSearchAnsibleHostsSuggestions({
        variables,
      }).catch(reportIfNotAborted);
    }

    if (groupBy === AnsibleGroupBy.Tasks) {
      loadSearchAnsibleTasksSuggestions({
        variables,
      }).catch(reportIfNotAborted);
    }
  };

  const handlePollingActiveSections = (fields: string[]) => {
    fieldsRef.current = fields;
    const input = {
      fullTextSearch: searchInput,
      predicates,
      fields,
    };

    const refetchPromise =
      groupBy === AnsibleGroupBy.Hosts
        ? hostsRefetch({
            input,
          })
        : tasksRefetch({
            input,
          });

    refetchPromise.catch(reportIfNotAborted);

    return refetchPromise;
  };

  useEffect(() => {
    loadSearchSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [predicates, searchInput]);

  // initial request with nullish fields
  useEffect(() => {
    loadSearchSuggestions(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deprecatedFilterNames = useMemo(
    () =>
      hiddenFilters ? [...DEPRECATED_FILTER_NAMES, ...hiddenFilters] : DEPRECATED_FILTER_NAMES,
    [hiddenFilters]
  );

  return (
    <Filters
      filters={filters}
      filtersItemsOptionsMap={filterOptionsMap}
      filtersLoading={hostsLoading || tasksLoading}
      // FYI: decided to removing sorting for now as we are migrating to new filter names and it can cause issues when switch host/tasks filtering
      sortOptions={[]}
      initialSortOption={null}
      initialSortDirection={null}
      pollActiveSections={handlePollingActiveSections}
      filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
      filtersType={filtersType}
      currentSavedView={currentSavedView}
      setCurrentSavedView={setCurrentSavedView}
      deprecatedFilterNames={deprecatedFilterNames}
      filtersDictionary={searchAnsibleHostsSuggestionsDictionary}
    >
      <FiltersSplit>
        <FiltersSidebar analyticsPage={AnalyticsPageStack.Ansible} />
        <FiltersContentWrapper>{children}</FiltersContentWrapper>
      </FiltersSplit>
    </Filters>
  );
};

export default memo(ConfigManagementFiltersLayout);
